import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { 
  BarChart3, 
  FileText, 
  FolderOpen, 
  TrendingUp,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Loader2
} from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../integrations/supabase/client'
import { useToast } from '../hooks/use-toast'
import { useTranslation } from 'react-i18next'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line
} from 'recharts'

interface MonthlyStats {
  month: string
  year: number
  activeAudits: number
  completedAudits: number
  totalFindings: number
  highSeverityFindings: number
  totalReports: number
}

export function StatisticsPage() {
  const [loading, setLoading] = useState(true)
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([])
  const { toast } = useToast()
  const { t, i18n } = useTranslation('common')
  const [searchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'overview'

  // Force i18n reload if needed
  useEffect(() => {
    if (i18n.isInitialized && !i18n.hasResourceBundle(i18n.language, 'common')) {
      i18n.reloadResources()
    }
  }, [i18n])

  // Hämta månadsvis statistik
  const fetchMonthlyStats = async () => {
    try {
      // Hämta data för de senaste 12 månaderna
      const { data: auditData, error: auditError } = await supabase
        .from('audits')
        .select('created_at, status, updated_at')
        .order('created_at', { ascending: true })

      if (auditError) throw auditError

      const { data: findingsData, error: findingsError } = await supabase
        .from('audit_findings')
        .select('created_at, severity, audit_id')
        .order('created_at', { ascending: true })

      if (findingsError) throw findingsError

      // Bearbeta data för att skapa månadsvis statistik
      const monthlyData = new Map<string, MonthlyStats>()
      const now = new Date()
      
      // Skapa de senaste 12 månaderna
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        const monthName = new Intl.DateTimeFormat(i18n.language || 'sv', { month: 'short' }).format(date)
        
        monthlyData.set(monthKey, {
          month: monthName,
          year: date.getFullYear(),
          activeAudits: 0,
          completedAudits: 0,
          totalFindings: 0,
          highSeverityFindings: 0,
          totalReports: 0
        })
      }

      // Lägg till audit data
      auditData?.forEach(audit => {
        const createdDate = new Date(audit.created_at)
        const monthKey = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`
        
        if (monthlyData.has(monthKey)) {
          const stats = monthlyData.get(monthKey)!
          
          if (audit.status === 'completed') {
            stats.completedAudits += 1
          } else {
            stats.activeAudits += 1
          }
        }
      })

      // Lägg till findings data
      findingsData?.forEach(finding => {
        const createdDate = new Date(finding.created_at)
        const monthKey = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`
        
        if (monthlyData.has(monthKey)) {
          const stats = monthlyData.get(monthKey)!
          stats.totalFindings += 1
          
          if (finding.severity === 'high' || finding.severity === 'critical') {
            stats.highSeverityFindings += 1
          }
        }
      })

      setMonthlyStats(Array.from(monthlyData.values()))
    } catch (error) {
      console.error('Error fetching monthly stats:', error)
      toast({
        title: t('statistics.errors.loadingTitle'),
        description: t('statistics.errors.loadingDescription'),
        variant: "destructive"
      })
    }
  }

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true)
      await fetchMonthlyStats()
      setLoading(false)
    }

    loadStats()
  }, [])

  if (loading) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
            <p className="text-gray-600">{t('statistics.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  const chartData = monthlyStats.map(stat => ({
    name: `${stat.month} ${String(stat.year).slice(-2)}`,
    [t('statistics.charts.activeAudits')]: stat.activeAudits,
    [t('statistics.charts.completedAudits')]: stat.completedAudits,
    [t('statistics.charts.findings')]: stat.totalFindings,
    [t('statistics.charts.criticalFindings')]: stat.highSeverityFindings
  }))

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('statistics.backToDashboard')}
              </Link>
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t('statistics.title')}</h1>
          <p className="text-lg text-gray-600">{t('statistics.description')}</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant={activeTab === 'overview' ? 'default' : 'outline'} 
            size="sm"
            asChild
          >
            <Link to="/statistics?tab=overview">{t('statistics.tabs.overview')}</Link>
          </Button>
          <Button 
            variant={activeTab === 'audits' ? 'default' : 'outline'} 
            size="sm"
            asChild
          >
            <Link to="/statistics?tab=audits">{t('statistics.tabs.audits')}</Link>
          </Button>
          <Button 
            variant={activeTab === 'findings' ? 'default' : 'outline'} 
            size="sm"
            asChild
          >
            <Link to="/statistics?tab=findings">{t('statistics.tabs.findings')}</Link>
          </Button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Kombinerad översikt */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                {t('statistics.overview.monthlyTitle')}
              </CardTitle>
              <CardDescription>
                {t('statistics.overview.monthlyDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={t('statistics.charts.completedAudits')} fill="#10b981" />
                    <Bar dataKey={t('statistics.charts.activeAudits')} fill="#3b82f6" />
                    <Bar dataKey={t('statistics.charts.findings')} fill="#f59e0b" />
                    <Bar dataKey={t('statistics.charts.criticalFindings')} fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Sammanfattning */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{t('statistics.summary.totalCompleted')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {monthlyStats.reduce((sum, stat) => sum + stat.completedAudits, 0)}
                </div>
                <p className="text-xs text-gray-500">{t('statistics.summary.last12Months')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{t('statistics.summary.activeAudits')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {monthlyStats.reduce((sum, stat) => sum + stat.activeAudits, 0)}
                </div>
                <p className="text-xs text-gray-500">{t('statistics.summary.last12Months')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{t('statistics.summary.totalFindings')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {monthlyStats.reduce((sum, stat) => sum + stat.totalFindings, 0)}
                </div>
                <p className="text-xs text-gray-500">{t('statistics.summary.last12Months')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{t('statistics.summary.criticalFindings')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {monthlyStats.reduce((sum, stat) => sum + stat.highSeverityFindings, 0)}
                </div>
                <p className="text-xs text-gray-500">{t('statistics.summary.last12Months')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Audits Tab */}
      {activeTab === 'audits' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              {t('statistics.audits.title')}
            </CardTitle>
            <CardDescription>
              {t('statistics.audits.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey={t('statistics.charts.completedAudits')} 
                    stroke="#10b981" 
                    strokeWidth={3}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey={t('statistics.charts.activeAudits')} 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Findings Tab */}
      {activeTab === 'findings' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {t('statistics.findings.title')}
            </CardTitle>
            <CardDescription>
              {t('statistics.findings.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey={t('statistics.charts.findings')} 
                    stroke="#f59e0b" 
                    strokeWidth={3}
                    dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey={t('statistics.charts.criticalFindings')} 
                    stroke="#ef4444" 
                    strokeWidth={3}
                    dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}